import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Search, Sparkles, Loader2, Wand2, Library, PlayCircle } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { speakText } from '@/components/lessons/speakText';

const GRADES = ['PK', 'K', '1', '2', '3', '4', '5'];
const SUGGESTIONS = ['CVC words', 'Short vowels', 'Sight words', 'Counting to 10', 'Shapes', 'Rhyming words'];

const gradeOf = (child) => {
  const g = (child?.grade_level || '').toString().toUpperCase();
  if (['PK', 'PRE-K', 'PREK'].includes(g)) return 'PK';
  if (!g || ['K', 'KG', 'KINDERGARTEN'].includes(g)) return 'K';
  return g.replace(/[^0-9]/g, '') || 'K';
};

const ResultCard = ({ icon, title, subtitle, onClick, testId, cta }) => (
  <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}>
    <Card className="hover:shadow-md hover:-translate-y-0.5 transition-all border-l-4 border-l-teal-400 cursor-pointer" onClick={onClick} data-testid={testId}>
      <CardContent className="p-4 flex items-center gap-3">
        <div className="w-12 h-12 rounded-xl bg-teal-50 border border-teal-100 flex items-center justify-center text-teal-700 font-fredoka font-bold text-sm text-center leading-tight overflow-hidden">
          {(icon || '✨').split('\n')[0].slice(0, 6)}
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-semibold text-slate-800 truncate">{title}</p>
          <p className="text-xs text-slate-500 truncate">{subtitle}</p>
        </div>
        <span className="text-xs font-semibold text-teal-700 flex items-center gap-1 shrink-0"><PlayCircle className="w-4 h-4" /> {cta}</span>
      </CardContent>
    </Card>
  </motion.div>
);

const LessonSearchPage = () => {
  const { childId } = useParams();
  const kidMode = Boolean(childId);
  const navigate = useNavigate();
  const { api } = useAuth();
  const [q, setQ] = useState('');
  const [grade, setGrade] = useState('K');
  const [results, setResults] = useState(null);
  const [searching, setSearching] = useState(false);
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    if (kidMode) api.get(`/children/${childId}`).then((r) => setGrade(gradeOf(r.data))).catch(() => {});
  }, [childId]); // eslint-disable-line react-hooks/exhaustive-deps

  const runSearch = async (term) => {
    const query = (term ?? q).trim();
    setQ(query);
    if (!query) return;
    setSearching(true);
    try {
      const r = await api.get('/lessons/search', { params: { q: query, child_id: childId } });
      setResults(r.data);
      if (kidMode) speakText(r.data.catalog.length + r.data.library.length ? `Here are lessons about ${query}` : `Let's make a lesson about ${query}!`);
    } catch { toast.error('Search failed — please try again'); }
    finally { setSearching(false); }
  };

  const create = async (topic) => {
    setCreating(true);
    if (kidMode) speakText(`Zazu is making your lesson about ${topic}`);
    try {
      const r = await api.post('/lessons/generate', { topic, grade, child_id: childId });
      toast.success('Lesson created!');
      navigate(kidMode ? `/lesson/${childId}/${r.data.id}` : `/lessons/library?open=${r.data.id}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not create the lesson');
    } finally { setCreating(false); }
  };

  const openCatalog = (c) => {
    if (kidMode) { navigate(`/explore/${childId}/${c.module}/${c.concept_key}`); return; }
    const topic = c.title.replace(/^What (is|are) (a |an )?/i, '').replace(/\?$/, '');
    if (window.confirm(`Create a new lesson about “${topic}”? It takes about 20 seconds.`)) create(topic);
  };
  const openLibrary = (l) => navigate(kidMode ? `/lesson/${childId}/${l.id}` : `/lesson/preview/${l.id}`);
  const empty = results && !results.catalog.length && !results.library.length;

  return (
    <div className="min-h-screen bg-gradient-to-b from-teal-50/60 via-white to-amber-50/30" data-testid="lesson-search-page">
      <header className="border-b border-white/40 bg-white/70 backdrop-blur sticky top-0 z-30">
        <div className="max-w-3xl mx-auto px-4 py-3 flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate(-1)} data-testid="lesson-search-back-btn"><ArrowLeft className="w-5 h-5" /></Button>
          <div className="flex-1">
            <h1 className="font-fredoka text-xl font-bold text-slate-900">{kidMode ? 'Find a lesson' : 'Search & create lessons'}</h1>
            <p className="text-xs text-slate-500">Search any topic — or let Zazu make a brand new lesson.</p>
          </div>
          {!kidMode && (
            <Button variant="outline" onClick={() => navigate('/lessons/library')} data-testid="lesson-search-library-btn"><Library className="w-4 h-4 mr-2" /> My library</Button>
          )}
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-8 space-y-6">
        <form className="flex gap-2" onSubmit={(e) => { e.preventDefault(); runSearch(); }}>
          <div className="relative flex-1">
            <Search className="w-5 h-5 absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Try “CVC words” or “counting to 10”"
              className={`pl-11 rounded-full bg-white ${kidMode ? 'h-14 text-lg' : 'h-12'}`} data-testid="lesson-search-input" />
          </div>
          {!kidMode && (
            <Select value={grade} onValueChange={setGrade}>
              <SelectTrigger className="w-28 h-12 rounded-full bg-white" data-testid="lesson-search-grade-select"><SelectValue /></SelectTrigger>
              <SelectContent>{GRADES.map((g) => <SelectItem key={g} value={g}>Grade {g}</SelectItem>)}</SelectContent>
            </Select>
          )}
          <Button type="submit" className={`rounded-full bg-teal-600 hover:bg-teal-700 ${kidMode ? 'h-14 px-6' : 'h-12'}`} disabled={searching} data-testid="lesson-search-submit-btn">
            {searching ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Search'}
          </Button>
        </form>

        {!results && (
          <div className="flex flex-wrap gap-2" data-testid="lesson-search-suggestions">
            {SUGGESTIONS.map((s) => (
              <button key={s} type="button" onClick={() => runSearch(s)} className="rounded-full bg-white border border-teal-200 px-4 py-2 text-sm text-teal-800 hover:bg-teal-50 transition-colors">{s}</button>
            ))}
          </div>
        )}

        {results && (
          <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
            className="rounded-3xl bg-gradient-to-br from-amber-100 via-amber-50 to-teal-50 border-2 border-amber-200 p-5 flex flex-col sm:flex-row items-start sm:items-center gap-4" data-testid="lesson-create-card">
            <div className="w-12 h-12 rounded-2xl bg-amber-400 flex items-center justify-center shrink-0"><Wand2 className="w-6 h-6 text-white" /></div>
            <div className="flex-1">
              <p className="font-fredoka text-lg font-bold text-slate-900">{empty ? 'No lessons yet — make one!' : 'Want a brand new lesson?'}</p>
              <p className="text-sm text-slate-600">Zazu builds a lesson about “{results.query}”: teaching, a video, questions, say-it-out-loud and tracing.</p>
            </div>
            <Button onClick={() => create(results.query)} disabled={creating} className="rounded-full bg-amber-500 hover:bg-amber-600 text-white" data-testid="lesson-create-btn">
              {creating ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Making it…</> : <><Sparkles className="w-4 h-4 mr-2" /> Create lesson</>}
            </Button>
          </motion.div>
        )}

        {results?.library.length > 0 && (
          <section className="space-y-3" data-testid="lesson-search-library-results">
            <h2 className="text-sm font-bold uppercase tracking-wide text-slate-500">{kidMode ? 'Your lessons' : 'From your library'}</h2>
            {results.library.map((l) => (
              <ResultCard key={l.id} icon={l.illustration} title={l.title} subtitle={`${l.topic} · Grade ${l.grade}`} cta={kidMode ? 'Start' : 'Preview'}
                onClick={() => openLibrary(l)} testId={`lesson-result-library-${l.id}`} />
            ))}
          </section>
        )}

        {results?.catalog.length > 0 && (
          <section className="space-y-3" data-testid="lesson-search-catalog-results">
            <h2 className="text-sm font-bold uppercase tracking-wide text-slate-500">Zazu topics</h2>
            {results.catalog.map((c) => (
              <ResultCard key={`${c.module}:${c.concept_key}`} icon={c.illustration} title={c.title} subtitle={c.module} cta={kidMode ? 'Learn' : 'Make lesson'}
                onClick={() => openCatalog(c)} testId={`lesson-result-catalog-${c.module}-${c.concept_key}`} />
            ))}
          </section>
        )}
      </main>
    </div>
  );
};

export default LessonSearchPage;
